import { useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown, Search } from "lucide-react";
import { displayName, personInitials, type Person } from "@/lib/board/types";

/**
 * Who the board is filtered to.
 *
 * One button opens a searchable list. A click shows just that person's
 * cards. A click on someone else switches to them, and a click on the
 * active person clears it. Shift-click (or the compare toggle) picks
 * several. The rules live in lib/board/filters.ts — this only presents them.
 * Every task board uses this; there is no second chip row.
 */
export function PeopleFilter({
  people, selected, compare, counts, onClick, onShowAll,
}: {
  people: Person[];
  selected: string[];
  compare: boolean;
  /** Open cards per person. Done and archived cards are already left out. */
  counts: Map<string, number>;
  onClick: (id: string, additive: boolean) => void;
  onShowAll: () => void;
}) {
  const uid = useId();
  const listId = `${uid}-people`;
  const panelId = `${uid}-panel`;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const [placement, setPlacement] = useState<Placement | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const focused = useRef(false);

  const sorted = useMemo(() => [...people].sort((a, b) => {
    const byCount = (counts.get(b.id) ?? 0) - (counts.get(a.id) ?? 0);
    if (byCount !== 0) return byCount;
    return displayName(a).localeCompare(displayName(b)) || a.id.localeCompare(b.id);
  }), [people, counts]);

  const q = query.trim().toLowerCase();
  const shown = q ? sorted.filter(p => displayName(p).toLowerCase().includes(q)) : sorted;
  // Index 0 is All. The people follow, already sorted.
  const entryCount = shown.length + 1;
  const activeIndex = Math.min(active, Math.max(0, entryCount - 1));

  const selectedPeople = selected
    .map(id => people.find(p => p.id === id))
    .filter((p): p is Person => !!p);

  useEffect(() => {
    if (!open) {
      setPlacement(null);
      return;
    }
    function place() {
      if (window.innerWidth < 768) {
        setPlacement({ kind: "sheet" });
        return;
      }
      const r = triggerRef.current?.getBoundingClientRect();
      if (!r) {
        setPlacement({ kind: "sheet" });
        return;
      }
      const width = Math.min(352, window.innerWidth - 16);
      let left = r.left;
      if (left + width > window.innerWidth - 8) left = Math.max(8, window.innerWidth - width - 8);
      const gap = 4;
      const desired = 384;
      const below = window.innerHeight - r.bottom - gap - 8;
      const above = r.top - gap - 8;
      if (below >= 220 || below >= above) {
        setPlacement({
          kind: "popover",
          top: r.bottom + gap,
          left,
          width,
          maxHeight: Math.max(160, Math.min(desired, below)),
        });
      } else {
        const maxHeight = Math.max(160, Math.min(desired, above));
        setPlacement({
          kind: "popover",
          top: Math.max(8, r.top - gap - maxHeight),
          left,
          width,
          maxHeight,
        });
      }
    }
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      focused.current = false;
      return;
    }
    if (!placement || focused.current) return;
    inputRef.current?.focus();
    focused.current = true;
  }, [open, placement]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      e.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    // Click, not pointerdown: a press that closes the menu must not fall
    // through onto the button underneath and open it again.
    function onClick(e: MouseEvent) {
      const t = e.target;
      if (!(t instanceof Node)) return;
      if (triggerRef.current?.contains(t)) return;
      if (panelRef.current?.contains(t)) return;
      setOpen(false);
    }
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [open]);

  useEffect(() => {
    if (!open || placement?.kind !== "sheet") return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open, placement?.kind]);

  const activeDomId = optionDomId(uid, activeIndex === 0 ? "all" : shown[activeIndex - 1]?.id ?? "all");
  useEffect(() => {
    if (!open) return;
    const el = document.getElementById(activeDomId);
    try { el?.scrollIntoView({ block: "nearest" }); } catch { /* jsdom has no layout */ }
  }, [open, activeDomId]);

  if (people.length === 0) return null;

  function openMenu() {
    setQuery("");
    setActive(highlightFor(sorted, selected));
    setOpen(true);
  }

  function pick(id: string, additive: boolean, restoreFocus: boolean) {
    onClick(id, additive);
    if (additive || compare) return;
    setOpen(false);
    if (restoreFocus) triggerRef.current?.focus();
  }

  function showAll(restoreFocus: boolean) {
    if (selected.length > 0) onShowAll();
    setOpen(false);
    if (restoreFocus) triggerRef.current?.focus();
  }

  function onSearchKey(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const current = Math.min(active, entryCount - 1);
      const next = e.key === "ArrowDown" ? current + 1 : current - 1;
      setActive(Math.min(entryCount - 1, Math.max(0, next)));
      return;
    }
    if (e.key !== "Enter") return;
    e.preventDefault();
    const current = Math.min(active, entryCount - 1);
    if (current === 0) showAll(true);
    else {
      const person = shown[current - 1];
      if (person) pick(person.id, e.shiftKey, true);
    }
  }

  function onQuery(value: string) {
    setQuery(value);
    const n = value.trim()
      ? sorted.filter(p => displayName(p).toLowerCase().includes(value.trim().toLowerCase())).length
      : sorted.length;
    setActive(value.trim() && n > 0 ? 1 : 0);
  }

  const label = triggerText(selectedPeople.map(displayName));
  const badge = triggerBadge(selectedPeople, counts);
  const described = selectedPeople.length === 0
    ? "People: All"
    : selectedPeople.length === 1
      ? `People: ${displayName(selectedPeople[0])}, ${counts.get(selectedPeople[0].id) ?? 0} open ${
        (counts.get(selectedPeople[0].id) ?? 0) === 1 ? "card" : "cards"
      }`
      : `People: ${selectedPeople.map(displayName).join(", ")}`;

  return (
    <div className="relative mt-2 inline-flex max-w-full">
      <button
        ref={triggerRef}
        type="button"
        className="btn btn-ghost btn-sm inline-flex min-h-11 max-w-full items-center gap-1.5 md:min-h-0"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={described}
        onClick={() => (open ? setOpen(false) : openMenu())}
        onKeyDown={e => {
          if (open) return;
          if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault();
            openMenu();
          }
        }}
      >
        <span className="max-w-[14rem] truncate">{label}</span>
        {badge !== null && (
          <span className="chip tabular-nums" title={badgeTitle(selectedPeople, counts)}>{badge}</span>
        )}
        <ChevronDown size={14} aria-hidden className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && placement && createPortal(
        <>
        <div className="fixed inset-0 z-50 bg-black/40 md:bg-transparent" aria-hidden />
        <div
          ref={panelRef}
          id={panelId}
          data-placement={placement.kind}
          style={placement.kind === "popover" ? {
            top: placement.top,
            left: placement.left,
            width: placement.width,
            maxHeight: placement.maxHeight,
          } : undefined}
          className={placement.kind === "sheet"
            ? "fixed inset-x-0 bottom-0 z-[60] flex max-h-[min(80dvh,32rem)] flex-col overflow-hidden rounded-t-2xl bg-white pb-[max(0.75rem,env(safe-area-inset-bottom))] shadow-soft ring-1 ring-line"
            : "fixed z-[60] flex flex-col overflow-hidden rounded-xl bg-white shadow-soft ring-1 ring-line"}
        >
          {placement.kind === "sheet" && (
            <div className="flex justify-center pt-2" aria-hidden>
              <span className="h-1 w-10 rounded-full bg-line" />
            </div>
          )}
          <div className="border-b border-line p-2">
            <div className="relative">
              <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray2" aria-hidden />
              <input
                ref={inputRef}
                type="text"
                className="input pl-9"
                aria-label="Search people"
                aria-controls={listId}
                aria-activedescendant={activeDomId}
                aria-autocomplete="list"
                placeholder="Search people"
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                autoFocus
                value={query}
                onChange={e => onQuery(e.target.value)}
                onKeyDown={onSearchKey}
              />
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
            <div
              id={listId}
              role="listbox"
              aria-label={compare ? "Compare people (pick several)" : "Show one person's cards"}
              aria-multiselectable="true"
              className="py-1"
            >
              <PersonRow
                id={optionDomId(uid, "all")}
                active={activeIndex === 0}
                selected={selected.length === 0}
                label="All"
                onMouseEnter={() => setActive(0)}
                onPick={() => showAll(false)}
              />
              {shown.map((p, i) => {
                const count = counts.get(p.id) ?? 0;
                const on = selected.includes(p.id);
                return (
                  <PersonRow
                    key={p.id}
                    id={optionDomId(uid, p.id)}
                    active={activeIndex === i + 1}
                    selected={on}
                    label={displayName(p)}
                    count={count}
                    initials={personInitials(p)}
                    ai={p.employee_kind === "ai"}
                    onMouseEnter={() => setActive(i + 1)}
                    onPick={shift => pick(p.id, shift, false)}
                  />
                );
              })}
            </div>
            {shown.length === 0 && (
              <p className="px-3 pb-3 text-center text-sm text-gray2">No one matches.</p>
            )}
          </div>
          <p className="border-t border-line px-3 py-2 text-xs text-gray2">
            {compare ? "Compare is on — click to add or remove." : "Click to choose one. Shift-click to pick several."}
          </p>
        </div>
        </>,
        document.body,
      )}
    </div>
  );
}

type Placement =
  | { kind: "sheet" }
  | { kind: "popover"; top: number; left: number; width: number; maxHeight: number };

function optionDomId(uid: string, key: string): string {
  return `${uid}-opt-${key}`;
}

/** All is index 0. Otherwise land on the person picked most recently. */
function highlightFor(sorted: Person[], selected: string[]): number {
  if (selected.length === 0) return 0;
  const idx = sorted.findIndex(p => p.id === selected[selected.length - 1]);
  return idx === -1 ? 0 : idx + 1;
}

function triggerText(names: string[]): string {
  if (names.length === 0) return "People: All";
  if (names.length === 1) return names[0];
  if (names.length === 2) return names.join(", ");
  return `${names[0]} +${names.length - 1}`;
}

function triggerBadge(selectedPeople: Person[], counts: Map<string, number>): string | null {
  if (selectedPeople.length === 0) return null;
  if (selectedPeople.length === 1) return String(counts.get(selectedPeople[0].id) ?? 0);
  return String(selectedPeople.length);
}

function badgeTitle(selectedPeople: Person[], counts: Map<string, number>): string {
  if (selectedPeople.length === 1) {
    const n = counts.get(selectedPeople[0].id) ?? 0;
    return `${n} open ${n === 1 ? "card" : "cards"}`;
  }
  return `${selectedPeople.length} people`;
}

function PersonRow({
  id, active, selected, label, count, initials, ai, onMouseEnter, onPick,
}: {
  id: string;
  active: boolean;
  selected: boolean;
  label: string;
  count?: number;
  initials?: string;
  ai?: boolean;
  onMouseEnter: () => void;
  onPick: (shift: boolean) => void;
}) {
  const name = count === undefined ? label : `${label} ${count}`;
  return (
    <button
      id={id}
      type="button"
      role="option"
      aria-selected={selected}
      aria-label={name}
      tabIndex={-1}
      onMouseDown={e => e.preventDefault()}
      onMouseEnter={onMouseEnter}
      onClick={e => onPick(e.shiftKey)}
      className={`flex min-h-11 w-full items-center gap-2.5 px-3 text-left text-sm ${
        active ? "bg-teal-mint" : "hover:bg-offwhite"
      }`}
    >
      <span
        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded ${
          selected ? "bg-teal-dark text-white" : "bg-white ring-1 ring-line"
        }`}
        aria-hidden
      >
        {selected ? <Check size={12} strokeWidth={3} /> : null}
      </span>
      {initials !== undefined && (
        <span
          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${
            ai ? "bg-ink text-white" : "bg-teal-mint text-teal-dark"
          }`}
          aria-hidden
        >
          {initials}
        </span>
      )}
      <span className={`min-w-0 flex-1 truncate ${selected ? "font-semibold" : "font-medium"}`}>{label}</span>
      {count !== undefined && <span className="shrink-0 text-xs tabular-nums text-gray2">{count}</span>}
    </button>
  );
}
